/**
 * Recipe collection card — DESIGN_GUIDELINES.md §9.1.
 *
 * Mantine supplies Card/Text/Badge/ActionIcon; Tailwind handles the grid placement and spacing.
 */

import { Link } from 'react-router-dom';
import { ActionIcon, Badge, Card, Group, Stack, Text } from '@mantine/core';
import { imageUrl, type CollectionItem } from '../lib/api';

function minutesLabel(minutes: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

export function RecipeCard({
  item,
  onToggleFavourite,
}: {
  item: CollectionItem;
  onToggleFavourite: (next: boolean) => void;
}) {
  const src = imageUrl(item.imageKey);

  return (
    <Card
      withBorder
      radius="lg"
      padding="md"
      style={{ boxShadow: 'var(--ff-shadow-sm)', position: 'relative' }}
    >
      {/* Images come from /api/images — after import the app never requests a HelloFresh URL. */}
      <Card.Section>
        <Link to={`/recipes/${item.id}`} aria-label={item.name}>
          {src ? (
            <img
              src={src}
              alt={item.name}
              loading="lazy"
              style={{
                display: 'block',
                width: '100%',
                aspectRatio: '4 / 3',
                objectFit: 'cover',
                background: 'var(--ff-offwhite)',
              }}
            />
          ) : (
            <div
              aria-hidden="true"
              style={{
                width: '100%',
                aspectRatio: '4 / 3',
                background: 'var(--ff-offwhite)',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--ff-muted)',
                fontSize: 13,
              }}
            >
              No image
            </div>
          )}
        </Link>
      </Card.Section>

      <Stack gap="xs" mt="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
          <Text
            fw={600}
            lineClamp={2}
            style={{ fontSize: 17 }}
            component={Link}
            to={`/recipes/${item.id}`}
            c="var(--ff-ink)"
            td="none"
          >
            {item.name}
          </Text>

          <ActionIcon
            variant={item.isFavourite ? 'filled' : 'subtle'}
            color={item.isFavourite ? 'red' : 'gray'}
            radius="xl"
            size="lg"
            aria-pressed={item.isFavourite}
            aria-label={item.isFavourite ? 'Remove from favourites' : 'Add to favourites'}
            onClick={() => onToggleFavourite(!item.isFavourite)}
          >
            <span aria-hidden="true">{item.isFavourite ? '♥' : '♡'}</span>
          </ActionIcon>
        </Group>

        {item.headline ? (
          <Text size="sm" c="var(--ff-muted)" lineClamp={2}>
            {item.headline}
          </Text>
        ) : null}

        <Group gap="xs" mt={4}>
          <Badge variant="light" color="gray" tt="none">
            {minutesLabel(item.totalMinutes)}
          </Badge>
          {item.difficultyLabel ? (
            <Badge variant="light" color="gray" tt="none">
              {item.difficultyLabel}
            </Badge>
          ) : null}
          <Badge variant="light" color="gray" tt="none">
            {item.defaultServings} servings
          </Badge>
          {/*
            A tier 2/3 import loses per-serving quantities. Surfaced rather than silently
            presenting a degraded recipe (DESIGN_GUIDELINES.md §3.5).
          */}
          {item.isPartial ? (
            <Badge variant="light" color="yellow" tt="none">
              Reduced detail
            </Badge>
          ) : null}
        </Group>
      </Stack>
    </Card>
  );
}
